import DTMFAudio from '@/libs/dtmfAudio'
import { v5 as uuidv5 } from 'uuid'
import socketClient from '@/libs/socketClient'
import { TEXT_CHANNEL, MAIL_CHANNEL, VOICE_CHANNEL } from '@/constants/channels'
import { ROUTE_ATTENDANCE_ITEM, ROUTE_ATTENDANCE_NEW } from '@/router/constants'
import { getVersion } from '@/libs/version'
import { getContactNumber, isVonixPhoneFocused, mainWindowToForeground } from '@/libs/helpers'
import events from '@/libs/events'
import { parseContactInfo } from '@/libs/contactInfo'
import Notify from 'quasar/src/plugins/Notify.js';
import { shouldDropChannels } from '@/libs/systemPause'

let ws = null
let connecting = false

const router = document.router
const busEvents = events()
const root = { root: true }
const timers = {}
const SET_CONNECTED = 'SET_CONNECTED'
const openAttendanceOffer = 'open-attendance-offer'
const cancelAttendanceOffer = 'cancel-attendance-offer'
const ipcRenderer = window.ipcRenderer

// O backend sempre envia status OFFERING em ofertas válidas; um chat já
// encerrado pode chegar aqui por inconsistência entre cache e banco.
const TERMINAL_CHAT_STATUSES = ['FINISHED', 'LOSED', 'ABANDONED', 'ERROR', 'ONSURVEY', 'AUTO_CLOSE']

const EVENT_ALERT = 'alert'
const EVENT_NEW_OFFERING = 'newOffering'
const EVENT_CANCEL_OFFERING = 'cancelOffering'
const EVENT_ATTENDANCE = 'attendance'
const EVENT_ATTENDANCES = 'attendances'
const EVENT_MESSAGE = 'message'
const EVENT_FINISHED = 'finished'
const EVENT_ON_RENEW_CHAT = 'onRenewChat'
const EVENT_CHAT_FINISHED = 'chatFinished'
const EVENT_CANCEL_TRANSFER = 'cancelTransfer'
const EVENT_SUCCESS_TRANSFER = 'successTransfer'
const EVENT_ERROR_START_CHAT = 'errorStartChat'
const EVENT_SENDED_MAIL = 'SENDED_MAIL'
const EVENT_READED_MESSAGE = 'READED_MESSAGE'
const EVENT_ONGOING_ATTENDANCE_MAIL = 'ongoingAttendanceMail'
const EVENT_FINISHED_MAIL = 'FINISHED_MAIL'
const EVENT_TRANSFERRED_MAIL = 'TRANSFERRED_MAIL'
const EVENT_GIVEUP_MAIL = 'GIVEUP_MAIL'
const EVENT_UPDATE_HISTORY_MAIL = 'UPDATE_HISTORY_MAIL'
const EVENT_ADD_ATTENDANCES = 'addAttendances'
const EVENT_CLEAR_ATTENDANCES = 'clearAttendances'
const EVENT_CHAT_ABANDONED = 'chatAbandoned'
const EVENT_SYNC_START = 'syncStart'
const EVENT_SYNC_COMPLETE = 'syncComplete'
const EVENT_ERROR_SEND_TEMPLATE = 'errorSendTemplate'
const META_ERROR_MEDIA_UPLOAD = '131053'
const EVENT_ON_SCHEDULE_TEMPLATE = 'onScheduleTemplate'
const EVENT_ON_START_ATTENDANCE_API = 'onStartAttendanceAPI'

const knowEventTypes = [
  EVENT_ALERT,
  EVENT_NEW_OFFERING,
  EVENT_CANCEL_OFFERING,
  EVENT_ATTENDANCE,
  EVENT_ATTENDANCES,
  EVENT_MESSAGE,
  EVENT_FINISHED,
  EVENT_ON_RENEW_CHAT,
  EVENT_CHAT_FINISHED,
  EVENT_CANCEL_TRANSFER,
  EVENT_SUCCESS_TRANSFER,
  EVENT_ERROR_START_CHAT,
  EVENT_SENDED_MAIL,
  EVENT_READED_MESSAGE,
  EVENT_ONGOING_ATTENDANCE_MAIL,
  EVENT_FINISHED_MAIL,
  EVENT_TRANSFERRED_MAIL,
  EVENT_GIVEUP_MAIL,
  EVENT_UPDATE_HISTORY_MAIL,
  EVENT_ADD_ATTENDANCES,
  EVENT_CLEAR_ATTENDANCES,
  EVENT_CHAT_ABANDONED,
  EVENT_SYNC_START,
  EVENT_SYNC_COMPLETE,
  EVENT_ERROR_SEND_TEMPLATE,
  EVENT_ON_SCHEDULE_TEMPLATE,
  EVENT_ON_START_ATTENDANCE_API
]

const uuidDNS = '34789426-0259-11eb-adc1-0242ac120002'

const actions = {
  init (ctx) {
    if (connecting) return

    const { dispatch, rootGetters, rootState } = ctx
    const hasQueueChat = rootState?.agentModule?.session?.agent?.hasQueueChat
    if (!hasQueueChat) return

    const token = rootGetters['agentModule/token']
    if (!token) return

    connecting = true
    const agent = rootGetters['agentModule/agent']
    const customer = rootGetters['settingsModule/customer']
    const hostSocket = `wss://${customer}.chat.vonixcc.com.br/ws?agent=${agent}&token=${token}&version=${getVersion()}`

    const onMessage = async ({ data }) => {
      if (!data) return newNotification('ERRO', 'MENSAGEM VAZIA')

      const event = forceParseJSON(data)

      if (!knowEventTypes.includes(event.type)) return newNotification('ERRO', `Mensagem desconhecida: ${data}`)

      if (event.type === EVENT_ON_START_ATTENDANCE_API) return handleStartAttendanceApi(event.data, ctx)
      if (event.type === EVENT_CANCEL_OFFERING) return clearAttendanceRejected(ctx, event.data)
      if (event.type === EVENT_MESSAGE) return ctx.dispatch('attendanceModule/setTextMessage', event.data, root)
      if (event.type === EVENT_ALERT) return newAlertNotify(event.data)
      if (event.type === EVENT_CANCEL_TRANSFER) return newNotification('ERRO', 'Agente indisponível! Não foi possível transferir.')
      if (event.type === EVENT_ERROR_START_CHAT) return newNotification('ERRO', 'Não foi possível iniciar o chat, por favor tente novamente ou entre em contato com o suporte.')
      if (event.type === EVENT_ATTENDANCES) return ctx.dispatch('attendanceModule/setTextAttendances', event.data, root)
      if (event.type === EVENT_ADD_ATTENDANCES) return ctx.dispatch('attendanceModule/addTextAttendances', event.data, root)
      if (event.type === EVENT_CLEAR_ATTENDANCES) return ctx.dispatch('attendanceModule/clearTextAttendances', event.data, root)
      if (event.type === EVENT_ATTENDANCE) return ctx.dispatch('attendanceModule/setTextAttendance', event.data, root)
      if (event.type === EVENT_CHAT_ABANDONED) return ctx.dispatch('attendanceModule/handleLosedAttendances', event.data, root)
      if (event.type === EVENT_SYNC_START) return ctx.dispatch('attendanceModule/startSync', null, root)
      if (event.type === EVENT_SYNC_COMPLETE) return ctx.dispatch('attendanceModule/onSyncComplete', null, root)

      if (event.type === EVENT_ONGOING_ATTENDANCE_MAIL) {
        clearTimer(event.data.id)
        return ctx.dispatch('attendanceModule/ongoingMailAttendance', event, root)
      }

      if (event.type === EVENT_ERROR_SEND_TEMPLATE) {
        const error = metaErrors[event.data]

        // Erro de mídia (131053): o atendimento não é finalizado, mas o agente
        // precisa perceber a falha e reenviar o arquivo — aviso central e persistente
        // em vez da notificação do sistema.
        if (String(event.data) === META_ERROR_MEDIA_UPLOAD) {
          return newPersistentCenterNotify(error?.title ?? `META ERROR:${event.data}`, error?.body ?? 'Falha ao enviar a mídia.')
        }

        if (error) return newNotification(error.title, error.body)
        return newNotification(`META ERROR:${event.data}`, 'Falha ao enviar a mensagem devido a um erro desconhecido.')
      }

      if (event.type === EVENT_SENDED_MAIL) {
        if (event.data.temporaryId) clearTimer(event.data.temporaryId)
        if (event.data.emailAttendanceId) clearTimer(event.data.emailAttendanceId)
        ctx.dispatch('attendanceModule/finishSendEmail', event.data, root)
        return newNotification('SUCESSO', 'E-mail disparado')
      }

      if (event.type === EVENT_UPDATE_HISTORY_MAIL) {
        if (event.data.emailAttendanceId) clearTimer(event.data.emailAttendanceId)
        ctx.dispatch('attendanceModule/updateHistoryMail', event.data.emailAttendanceId, root)
        return newNotification('SUCESSO', 'Histórico do Atendimento atualizado')
      }

      if (event.type === EVENT_ON_RENEW_CHAT) {
        ctx.dispatch('attendanceModule/dropTextAttendance', event.data.id, root)
        const name = event.data.name || getContactNumber(event.data)

        return newNotification('SUCESSO', `Atendimento de ${name} foi Reaberto.`)
      }
      if (event.type === EVENT_CHAT_FINISHED) {
        const chatId = event.data.id
        const attendances = ctx.rootGetters['attendanceModule/getAttendances']
        const chatAttendance = attendances.find(x => x.id.toString() === chatId.toString())

        if (!chatAttendance) return

        ctx.dispatch('attendanceModule/finishAttendanceText', chatAttendance, root)

        if (event.data?.Error) {
          const number = getContactNumber(event.data)
          const eventError = tryParseJson(event.data?.Error)
          const timeoutEvent = eventError?.code === 0 && eventError?.message === ''
          if (timeoutEvent) return newNotification('ALERTA', 'Atendimento já finalizado por tempo esgotado.')

          const mappedError = metaErrors[eventError?.code]
          const isInternalError = eventError?.code === 500

          if (mappedError) return newNotification(mappedError.title, mappedError.body)
          if (isInternalError) newNotification('ERRO 500', 'Atendimento finalizado. Erro ao enviar mensagem para o Broker')

          return newNotification('ERRO', `Atendimento finalizado, Erro desconhecido. Número: ${number}`)
        }

        return newNotification('ALERTA', 'Atendimento já finalizado por tempo esgotado.')
      }

      if (event.type === EVENT_FINISHED_MAIL) {
        if (event.data.temporaryId) clearTimer(event.data.temporaryId)
        if (event.data.emailAttendanceId) clearTimer(event.data.emailAttendanceId)
        ctx.dispatch('attendanceModule/finishSendEmail', event.data, root)
        return newNotification('SUCESSO', 'Atendimento Finalizado')
      }

      if (event.type === EVENT_TRANSFERRED_MAIL) {
        if (event.data.temporaryId) clearTimer(event.data.temporaryId)
        if (event.data.emailAttendanceId) clearTimer(event.data.emailAttendanceId)
        ctx.dispatch('attendanceModule/finishSendEmail', event.data, root)
        return newNotification('SUCESSO', 'Atendimento Transferido')
      }

      if (event.type === EVENT_GIVEUP_MAIL) {
        if (event.data.temporaryId) clearTimer(event.data.temporaryId)
        if (event.data.emailAttendanceId) clearTimer(event.data.emailAttendanceId)
        ctx.dispatch('attendanceModule/giveUpEmail', event.data.emailAttendanceId, root)
        return newNotification('SUCESSO', 'Atendimento em Espera')
      }

      if (event.type === EVENT_FINISHED || event.type === EVENT_SUCCESS_TRANSFER) {
        const message = event.type === EVENT_FINISHED ? 'finalizado' : 'transferido'
        const { data: chat } = event
        const attendances = ctx.rootGetters['attendanceModule/getAttendances']
        const chatAttendance = attendances.find(x => x.id.toString() === chat.id.toString())
        ctx.dispatch('attendanceModule/finishingAttendanceText', false, root)

        if (!chatAttendance) return
        ctx.dispatch('attendanceModule/finishAttendanceText', chatAttendance, root)
        const name = chat?.name || parseContactInfo(chat.info)?.firstName || 'Cliente'
        return newNotification('SUCESSO', `Atendimento de ${name} foi ${message}.`)
      }

      if (event.type === EVENT_READED_MESSAGE) {
        const { data: message } = event
        const { id: messageId, readedAt, deliveredAt } = message

        const attendances = ctx.rootGetters['attendanceModule/getAttendances']
        const currentChatId = attendances.find(att => att.messages?.some(msg => msg.id === messageId))?.id
        if (!currentChatId) return
        return ctx.dispatch('attendanceModule/setReaded', {
          chatId: currentChatId,
          messageId,
          readedAt: readedAt ? readedAt * 1000 : null,
          deliveredAt: deliveredAt ? deliveredAt * 1000 : null
        }, root)
      }

      dispatch(event.type, event.data)
    }

    const onDisconnect = () => {
      ctx.commit(SET_CONNECTED, false)
    }

    const onConnect = () => {
      connecting = false
      ctx.commit(SET_CONNECTED, true)
    }

    const startConnection = () => {
      ws = socketClient(
        hostSocket,
        onMessage,
        onConnect,
        onDisconnect
      )
    }

    if (!ws) return startConnection()

    ws.drop()
    ws = null
    ctx.commit(SET_CONNECTED, false)
    return waitDisconnect().then(startConnection)
  },

  async newOffering (ctx, { autoAnswer, secsAnswer, chat, email }) {
    if (chat && TERMINAL_CHAT_STATUSES.includes(chat.status)) {
      console.warn(`[newOffering] Oferta ignorada: chat ${chat.id} com status ${chat.status}`)
      return
    }

    const attendanceId = chat ? chat.id : email.emailAttendanceId

    const rejectedChatMessage = { id: attendanceId, channel: chat ? TEXT_CHANNEL : MAIL_CHANNEL }
    const hasSchedulePause = ctx.rootGetters['agentModule/hasSchedulePause']
    if (hasSchedulePause) return ws.send({ action: 'onreject', chat: { ...rejectedChatMessage, reason: 'HAS A ACTION SCHEDULED: PAUSE' } })

    const hasScheduleLogout = ctx.rootGetters['agentModule/hasScheduleLogout']
    if (hasScheduleLogout) return ws.send({ action: 'onreject', chat: { ...rejectedChatMessage, reason: 'HAS A ACTION SCHEDULED: LOGOUT' } })

    const hasJourneyPrePause = ctx.rootGetters['agentModule/hasJourneyPrePause']
    if (hasJourneyPrePause) return ws.send({ action: 'onreject', chat: { ...rejectedChatMessage, reason: 'HAS A ACTION SCHEDULED: JOURNEY_PRE_PAUSE' } })

    const attendance = {}
    clearAttendance(ctx, attendanceId)

    const hasAutoRejection = ctx.rootGetters['agentModule/hasAutoRejection']

    if (hasAutoRejection) {
      const refuseChatOffer = ctx.rootGetters['settingsModule/hasChatRejection'] && chat
      const refuseMailOffer = ctx.rootGetters['settingsModule/hasMailRejection'] && email
      if (refuseChatOffer || refuseMailOffer) return ws.send({ action: 'onreject', chat: { ...rejectedChatMessage, reason: 'HAS A ACTION SCHEDULED: AUTOREJECTION' } })
    }

    if (chat) {
      Object.assign(attendance, chat)
      attendance.info = forceParseJSON(attendance.info)
      attendance.channel = TEXT_CHANNEL
      attendance.status = 'OFFERING'
      ctx.dispatch('attendanceModule/setTextAttendance', attendance, root)
    } else {
      Object.assign(attendance, {
        direction: email.direction,
        mailTo: email.mailTo,
        replyTo: email.replyTo,
        mailCc: email.mailCc,
        mailFrom: email.mailFrom,
        mailSubject: email.mailSubject,
        messageId: email.id,
        status: email.status,
        emailAttendanceId: email.emailAttendanceId,
        emailAnswerId: email.id,

        botId: email.botId,
        queueId: email.queueId,
        queueName: email.queueName,

        channel: MAIL_CHANNEL,
        isOffering: true,
        type: 'email',
        source: 'Email',
        sourceId: '',
        info: { firstName: email.mailFrom },
        messages: [{ id: email.mailSubject, message: email.mailSubject }]
      })

      ctx.dispatch('attendanceModule/setMailAttendance', attendance, root)
    }

    const attendances = ctx.rootGetters['attendanceModule/getAttendances']
    const hasVoiceAttendance = Object.values(attendances).some(({ channel }) => channel === VOICE_CHANNEL)

    if (autoAnswer) {
      const windowToForeground = ctx.rootGetters['settingsModule/windowToForeground']
      if (windowToForeground === 'yes') mainWindowToForeground()
      if (!hasVoiceAttendance) DTMFAudio.playNewChat()
      ctx.dispatch('acceptAttendance', { id: attendanceId, channel: attendance.channel })

      if (shouldKeepCurrentChatFocus(ctx, attendance)) {
        ctx.dispatch('attendanceModule/markChatAsUnread', attendanceId, root)
        ctx.dispatch('sendUnreadUpdate', { chatId: attendanceId, manualUnread: true })
        return
      }

      return router.push({ name: ROUTE_ATTENDANCE_ITEM, params: { attendanceId: attendanceId } }).catch(() => { })
    }
    const timeout = (secsAnswer || 30) * 1000
    timers[attendanceId] = setTimeout(() => {
      ctx.dispatch('rejectAttendance', attendance)
    }, timeout)

    if (!hasVoiceAttendance) {
      DTMFAudio.playChatRingback()
    }

    const offerAttendance = {
      id: attendanceId,
      source: attendance.source,
      queueId: attendance.queueId,
      channel: attendance.channel,
      phone: attendance.phone,
      sourceId: attendance.sourceId,
      messages: attendance.messages,
      queueName: attendance.queueName,
      contactName: concatName(attendance.info?.firstName, attendance.info?.lastName)
    }

    const queueProps = ctx.rootGetters['agentModule/queueProperties'](attendance.queueId)
    if (queueProps?.anonymizePhone === 'yes') {
      offerAttendance.anonymizePhone = true
    }

    isVonixPhoneFocused().then((hasFocusInVP) => {
      hasFocusInVP
        ? busEvents.emit(openAttendanceOffer, offerAttendance)
        : ipcRenderer.send(openAttendanceOffer, offerAttendance)
    })
  },

  finish () {
    ws?.close()
    ws = null
  },

  sendMessage ({ dispatch }, { message, attendanceId, type, parentRef, parentMessage }) {
    const createdAt = Date.now()
    const chatId = Number(attendanceId)
    const temporaryId = uuidv5(`${chatId}|${createdAt}|${message}`, uuidDNS)
    const localPayload = { message, chatId, temporaryId, createdAt, type, parentRef, parentMessage }
    const remotePayload = { message, chatId, temporaryId, createdAt, type, parentRef }
    dispatch('attendanceModule/setAgentText', localPayload, root)
    ws.send({ action: 'onmessage', ...remotePayload })
  },

  sendMail ({ dispatch, rootState }, email) {
    const attendanceId = email.temporaryId ?? email.emailAttendanceId
    dispatch('attendanceModule/setAttendanceSending', attendanceId, root)

    clearTimer(attendanceId)
    timers[attendanceId] = setTimeout(() => {
      clearTimer(attendanceId)
      dispatch('attendanceModule/setAttendanceNotSended', attendanceId, root)
    }, 30000)

    const agentId = rootState?.agentModule?.session?.agent?.id

    ws.send({
      agentId,
      action: 'onmessage',
      type: 'SEND_MAIL',
      email: {
        to: email.to,
        cc: email.cc,
        agentId,
        botId: email.botId,
        mailFrom: email.mailFrom,
        subject: email.subject,
        message: email.message,
        createdAt: email.createdAt,
        temporaryId: email.temporaryId,
        attachments: email.attachments,
        answerOption: email.answerOption,
        emailParentId: email.emailParentId,
        transferQueueId: email.transferQueueId,
        transferAgentId: email.transferAgentId,
        emailAttendanceId: email.emailAttendanceId
      }
    })
  },

  startChat (_, { templateId, sourceId, agentId, type, queueId, fields, header }) {
    const temporaryId = uuidv5(`${sourceId}|${templateId}`, uuidDNS)
    ws.send({ action: 'onmessage', templateId, sourceId, agentId, queueId, temporaryId, type, fields: fields ?? {}, header: header ?? {} })
  },

  scheduleTemplate ({ rootState }, { scheduleId, phoneNumber, templateToSend, start }) {
    if (!ws) {
      Notify.create({
        message: 'O template não pôde ser agendado. Por favor despause para realizar um novo agendamento',
        textColor: 'white',
        position: 'bottom',
        icon: 'mdi-alert',
        timeout: 5000,
        color: 'negative'
      })
    }
    const agentId = rootState?.agentModule?.session?.agent?.id
    const queueId = templateToSend.queueId
    const header = templateToSend.header ?? {}
    const fields = templateToSend.fields ?? {}
    const message = {
      templateId: templateToSend.id,
      sourceId: phoneNumber,
      agentId,
      queueId,
      type: 'schedule-template',
      fields: fields ?? {},
      header: header ?? {},
      scheduleId,
      timestamp: start
    }
    ws.send({ action: 'onmessage', ...message })
  },

  deleteScheduledTemplate (ctx, { scheduleId }) {
    ws.send({ action: 'onmessage', scheduleId, type: 'delete-schedule-template' })
  },

  setReaded (ctx, event) {
    const chatId = Number(event.attendanceId)
    const agentId = ctx.rootState?.agentModule?.session?.agent?.id
    const messageId = Number(event.messageId)
    const message = ctx.rootState.attendanceModule.current[chatId]?.messages?.find((msg) => msg.id === messageId)
    if (message?.readedAt) return
    ctx.dispatch('attendanceModule/setReaded', { chatId, messageId }, root)
    ws.send({ action: 'onstatus', chatId, messageId, agentId, status: 'READED' })
  },

  setFinished (ctx, payload) {
    const chatId = typeof payload === 'object' ? payload.chatId : payload
    const retryCount = typeof payload === 'object' ? (payload.retryCount || 0) : 0

    ctx.dispatch('attendanceModule/finishingAttendanceText', { chatId, retryCount: retryCount + 1 }, root)
    ws.send({ action: 'onfinish', chatId })
  },

  sendTransfer (ctx, transfer) {
    const { chatId } = transfer
    const attendances = ctx.rootGetters['attendanceModule/getAttendances']
    const attendance = attendances.find(({ id }) => id.toString() === chatId.toString())

    if (attendance) {
      if (attendance.unreadMessages || attendance.manualUnread) {
        ctx.dispatch('attendanceModule/clearChatUnreadMark', chatId, { root: true })
        ctx.dispatch('sendUnreadUpdate', { chatId, manualUnread: false })
      }
    }

    ws.send({ action: 'ontransfer', ...transfer })
  },

  acceptAttendance (ctx, attendance) {
    DTMFAudio.stop()
    clearAttendance(ctx, attendance.id)
    ws.send({ action: 'onaccept', chat: attendance })
  },

  rejectAttendance (ctx, chat) {
    DTMFAudio.stop()
    const attendances = ctx.rootGetters['attendanceModule/getAttendances']
    const hasScheduleLogout = ctx.rootGetters['agentModule/hasScheduleLogout']
    const hasSchedulePause = ctx.rootGetters['agentModule/hasSchedulePause']
    const voiceAttendances = Object.values(attendances).filter(({ channel }) => channel === VOICE_CHANNEL)
    const canApplySchedulePause = Boolean(!voiceAttendances.length && hasSchedulePause)
    const canApplyScheduleLogout = Boolean(!voiceAttendances.length && hasScheduleLogout)
    const attendanceId = chat.emailAttendanceId ?? chat.id

    clearAttendanceRejected(ctx, attendanceId)
    ws.send({ action: 'onreject', chat })

    if (canApplySchedulePause) {
      ctx.dispatch('sipModule/pause', hasSchedulePause, { root: true })
      ctx.dispatch('socketModule/finish', null, { root: true })
    }

    if (canApplyScheduleLogout) {
      ctx.dispatch('sipModule/logout', null, { root: true })
      ctx.dispatch('socketModule/finish', null, { root: true })
    }
  },

  sendUnreadUpdate (ctx, { chatId, manualUnread }) {
    ws.send({
      action: 'on-update-unread',
      chatId,
      chat: {
        id: chatId,
        unread: manualUnread ? 1 : 0
      }
    })
  }
}

const middlewares = {
  'agentModule/UPDATE_AGENT': (ctx) => {
    const isPausedAgent = shouldDropChannels(ctx.state?.agentModule?.session?.agent)
    const isOfflineAgent = !ctx.state?.agentModule?.session?.agent?.logged

    const dropSocketConnection = isPausedAgent || isOfflineAgent
    const openSocketConnection = !ctx.state.socketModule.connected && !dropSocketConnection

    if (dropSocketConnection) ctx.dispatch('socketModule/finish', null, { root: true })
    if (openSocketConnection) ctx.dispatch('socketModule/init', null, { root: true })
  },

  'agentModule/SET_SESSION': (ctx, { payload }) => {
    const isPausedAgent = shouldDropChannels(ctx.state?.agentModule?.session?.agent)
    const isOfflineAgent = !ctx.state?.agentModule?.session?.agent?.logged

    const dropSocketConnection = isPausedAgent || isOfflineAgent
    const openSocketConnection = !ctx.state.socketModule.connected && !dropSocketConnection

    if (dropSocketConnection) ctx.dispatch('socketModule/finish', null, { root: true })
    if (openSocketConnection) ctx.dispatch('socketModule/init', null, { root: true })

    if (payload?.quickAnswers?.length) ctx.dispatch('quickAnswersModule/setAnswers', [...payload.quickAnswers], { root: true })
  }
}

const newNotification = (title, body) => {
  return new Notification(title, { body })
}

// O alerta precisa aparecer dentro da janela: newNotification usa a notificação
// do sistema, que o agente não vê quando o SO a bloqueia ou quando ela some
// sozinha. É por aqui que chega "Não foi possível abrir o atendimento.", o único
// aviso de que o chat não abriu. A notificação do sistema continua, porque ela é
// o único aviso quando a janela está atrás de outro aplicativo.
const newAlertNotify = (body) => {
  newNotification('ALERTA', body)

  return Notify.create({
    type: 'warning',
    message: body,
    caption: 'ALERTA',
    position: 'bottom',
    timeout: 8000,
    actions: [{ label: 'OK', color: 'black' }]
  })
}

const newPersistentCenterNotify = (title, body) => {
  return Notify.create({
    type: 'negative',
    message: title,
    caption: body,
    position: 'center',
    timeout: 0,
    actions: [{ label: 'OK', color: 'white' }]
  })
}

const concatName = (firstName, lastName) => {
  if (!lastName && !firstName) return 'Desconhecido'
  if (!lastName) return firstName
  if (!firstName) return lastName
  return `${firstName} ${lastName}`
}

const forceParseJSON = (str) => {
  try {
    return JSON.parse(str)
  } catch (err) {
    return {}
  }
}

export default {
  namespaced: true,

  actions,
  middlewares,

  mutations: {
    [SET_CONNECTED]: (state, connected) => (
      Object.assign(state, { connected })
    )
  },

  state: {
    connected: false
  }
}

const clearAttendanceRejected = (ctx, attendanceId) => {
  clearAttendance(ctx, attendanceId)
  ctx.dispatch('attendanceModule/dropTextAttendance', attendanceId, root)
}

const handleStartAttendanceApi = (data, ctx) => {
  const { botId, templateId, sourceId, queueId, params, header } = data

  ctx.commit('attendanceModule/SET_API_ATTENDANCE_DATA', {
    sourceId,
    botId,
    templateId,
    queueId,
    fields: params,
    header
  }, { root: true })

  router.push({ name: ROUTE_ATTENDANCE_NEW }).catch(() => { })
}

const clearAttendance = (ctx, attendanceId) => {
  clearTimer(attendanceId)
  busEvents.emit(cancelAttendanceOffer, { id: attendanceId })
  ipcRenderer.send(cancelAttendanceOffer, { id: attendanceId })

  const hasSchedulePause = ctx.rootGetters['agentModule/hasSchedulePause']
  const hasScheduleLogout = ctx.rootGetters['agentModule/hasScheduleLogout']

  if (hasSchedulePause) ctx.dispatch('agentModule/clearSchedulePause', null, { root: true })
  if (hasScheduleLogout) ctx.dispatch('agentModule/clearScheduleLogout', null, { root: true })
}

const clearTimer = (attendanceId) => {
  if (timers[attendanceId]) clearTimeout(timers[attendanceId])
  delete timers[attendanceId]
}

const shouldKeepCurrentChatFocus = (ctx, attendance) => {
  if (attendance.channel !== TEXT_CHANNEL) return false

  const queueProps = ctx.rootGetters['agentModule/queueProperties'](attendance.queueId)
  if (queueProps?.keepChatFocusOnAutoAnswer !== 'yes') return false

  const currentRoute = router.currentRoute
  if (currentRoute.name !== ROUTE_ATTENDANCE_ITEM) return false

  const currentAttendanceId = currentRoute.params.attendanceId
  if (!currentAttendanceId || String(currentAttendanceId) === String(attendance.id)) return false

  const attendances = ctx.rootGetters['attendanceModule/getAttendances']
  const currentAttendance = attendances.find(({ id, temporaryId }) => (
    [id, temporaryId].some(value => String(value) === String(currentAttendanceId))
  ))

  return currentAttendance?.channel === TEXT_CHANNEL
}

const waitDisconnect = () => new Promise((resolve) => setTimeout(resolve, 350))

const metaErrors = {
  0: { title: 'ERRO META 0', body: 'Não foi possível autenticar o usuário do app.' },
  1: { title: 'ERRO META 1', body: 'API desconhecida. Solicitação inválida ou possível erro do servidor.' },
  10: { title: 'ERRO META 10', body: 'Permissão negada. A permissão não foi concedida ou foi removida.' },
  100: { title: 'ERRO META 100', body: 'Solicitação POST não suportada. O objeto com o ID messages não existe, não pode ser carregado devido a permissões ausentes, ou não suporta esta operação.' },
  130429: { title: 'ERRO META 130429', body: 'Limite de volume atingido. A taxa de transferência de dados de mensagens da API de Nuvem foi atingida.' },
  130472: { title: 'ERRO META 130472', body: 'O número do usuário faz parte de um experimento do WhatsApp. Não é possível iniciar uma conversa com este número' },
  130497: { title: 'ERRO META 130497', body: 'A conta comercial está impedida de enviar mensagens a usuários neste país.' },
  131021: { title: 'ERRO META 131021', body: 'O destinatário não pode ser o remetente. O número de telefone do destinatário e do remetente é o mesmo.' },
  131026: { title: 'ERRO META 131026', body: 'Não foi possível entregar a mensagem. O número de telefone do destinatário não está registrado no WhatsApp ou o destinatário não aceitou os novos Termos de Serviço e a nova Política de Privacidade.' },
  131030: { title: 'ERRO META 131030', body: 'O número de telefone do destinatário não está na lista de permissão: Adicione o telefone do destinatário à lista de destinatários e tente novamente.' },
  131031: { title: 'ERRO META 131031', body: 'A conta do WhatsApp Business associada ao app foi restringida ou desabilitada por violar uma política da plataforma.' },
  131037: { title: 'ERRO META 131037', body: 'O nome de exibição do número fornecido pelo WhatsApp precisa ser aprovado antes que a mensagem possa ser enviada.' },
  131042: { title: 'ERRO META 131042', body: 'A Meta recusou enviar sua mensagem, isso está relacionado à tentativa de faturar conversas no cartão de crédito' },
  131045: { title: 'ERRO META 131045', body: 'Certificado incorreto. Ocorreu uma falha ao enviar a mensagem devido a um erro de registro do número de telefone.' },
  131047: { title: 'ERRO META 131047', body: 'Mensagem de novo envolvimento. Mais de 24 horas se passaram desde que o destinatário respondeu pela última vez ao número do remetente.' },
  131048: { title: 'ERRO META 131048', body: 'Limite de taxa de spam atingido. É possível que muitas mensagens anteriores tenham sido bloqueadas ou marcadas como spam.' },
  131049: { title: 'ERRO META 131049', body: 'Meta recusou enviar esse modelo (Marketing) para manter o engajamento saudável do ecossistema' },
  131050: { title: 'ERRO META 131050', body: 'O usuário não recebe mais mensagens de marketing. Não é possível entregar a mensagem.' },
  131051: { title: 'ERRO META 131051', body: 'Tipo de mensagem não compatível' },
  131052: { title: 'ERRO META 131052', body: 'Erro de download de mídia. Não foi possível baixar a mídia enviada pelo usuário.' },
  131053: { title: 'ERRO META 131053', body: 'Não foi possível carregar a mídia usada na mensagem. Tente novamente em alguns minutos' },
  131056: { title: 'ERRO META 131056', body: 'Limite de volume de envios de mensagens atingido' },
  132000: { title: 'ERRO META 132000', body: 'O número de valores de parâmetros variáveis incluídos na solicitação não corresponde ao número de parâmetros variáveis definidos no modelo.' },
  132001: { title: 'ERRO META 132001', body: 'O modelo não existe no idioma especificado ou não foi aprovado.' },
  132005: { title: 'ERRO META 132005', body: 'Texto hidratado do modelo longo demais' },
  132007: { title: 'ERRO META 132007', body: 'Política de caracteres do formato do modelo violada. O conteúdo do modelo viola uma política do WhatsApp.' },
  132012: { title: 'ERRO META 132012', body: 'Incompatibilidade no formato do parâmetro do modelo. Os valores de parâmetros variáveis foram formatados incorretamente.' },
  132015: { title: 'ERRO META 132015', body: 'Modelo pausado. O modelo foi pausado por baixa qualidade e não pode ser enviado em mensagens.' },
  132016: { title: 'ERRO META 132016', body: 'Modelo desabilitado. O modelo foi pausado muitas vezes por baixa qualidade e foi desabilitado de forma permanente.' },
  132068: { title: 'ERRO META 132068', body: 'Fluxo bloqueado. O Fluxo está no estado bloqueado.' },
  132069: { title: 'ERRO META 132069', body: 'Fluxo limitado. O Fluxo está no estado limitado, e 10 mensagens usando esse fluxo já foram enviadas na última hora.' },
  133000: { title: 'ERRO META 133000', body: 'Cancelamento do registro incompleto. Uma tentativa anterior de cancelamento do registro falhou.' },
  133004: { title: 'ERRO META 133004', body: 'Servidor temporariamente indisponível. O servidor está temporariamente indisponível.' },
  133005: { title: 'ERRO META 133005', body: 'Incompatibilidade do PIN na confirmação em duas etapas' },
  133006: { title: 'ERRO META 133006', body: 'Reverificação do número de telefone necessária. O número de telefone precisa ser verificado antes do registro.' },
  133008: { title: 'ERRO META 133008', body: 'Muitas tentativas do PIN de confirmação em duas etapas' },
  133009: { title: 'ERRO META 133009', body: 'Tentativa muito rápida do PIN de confirmação em duas etapas' },
  133010: { title: 'ERRO META 133010', body: 'Número de telefone não registrado. O número de telefone não está registrado na Plataforma do WhatsApp Business.' },
  133015: { title: 'ERRO META 133015', body: 'Aguarde alguns minutos antes de tentar registrar este número de telefone' },
  133016: { title: 'ERRO META 133016', body: 'Limite de taxa de registro/exclusão de contas excedido' },
  135000: { title: 'ERRO META 135000', body: 'Falha ao enviar a mensagem devido a um erro desconhecido com seus parâmetros de solicitação.' },
  190: { title: 'ERRO META 190', body: 'Erro de validação do token de acesso' },
  2: { title: 'ERRO META 2', body: 'Serviço de API. Há um erro temporário por inatividade ou sobrecarga.' },
  3: { title: 'ERRO META 3', body: 'Método de API indica um problema que envolve recursos ou permissões.' },
  33: { title: 'ERRO META 33', body: 'O valor do parâmetro é inválido. O número de telefone comercial foi excluído.' },
  4: { title: 'ERRO META 4', body: 'Muitas chamadas de API. O app atingiu o limite da taxa de chamadas de API.' },
  68: { title: 'ERRO META 68', body: 'Bloqueado temporariamente por violações de políticas. A conta do WhatsApp Business associada ao app foi restringida ou desabilitada por violar uma política da plataforma.' },
  80007: { title: 'ERRO META 80007', body: 'Problemas com limite de volume. A conta do WhatsApp Business atingiu o limite de volume.' }
}
const tryParseJson = (data) => {
  const FIRST = 0

  if (data == null) return null

  if (typeof data === 'object') {
    return Array.isArray(data) ? data[FIRST] : data
  }

  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data)
      return Array.isArray(parsed) ? parsed[FIRST] : parsed
    } catch {
      return data
    }
  }

  return data
}
